import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, deleteDoc, Timestamp 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Lubricantsinventory() {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form State
  const [formData, setFormData] = useState({ name: '', grade: '', company: '', price: '', stock: '' });
  const [editId, setEditId] = useState(null);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "lubricants"), where("userId", "==", userId));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData, price: parseFloat(formData.price), stock: parseInt(formData.stock), updatedAt: Timestamp.now() };
      if (editId) {
        await updateDoc(doc(db, "lubricants", editId), data);
        Toast.fire({ icon: 'success', title: 'Item Updated' });
      } else {
        await addDoc(collection(db, "lubricants"), { ...data, userId, createdAt: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'Item Added' });
      }
      resetForm();
      // eslint-disable-next-line no-undef
      $('#itemModal').modal('hide');
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', grade: '', company: '', price: '', stock: '' });
    setEditId(null);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Are you sure?', text: "Delete this item?", icon: 'warning', showCancelButton: true });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "lubricants", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  // Logic: Search & Pagination
  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  return (
    <div className="content-wrapper bg-light">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded">
          <h4 className="m-0 font-weight-bold text-navy"><i className="fas fa-oil-can mr-2"></i>Lubricants Inventory</h4>
          <button className="btn btn-primary btn-sm" onClick={resetForm} data-toggle="modal" data-target="#itemModal">
            <i className="fas fa-plus mr-1"></i> Add New Product
          </button>
        </div>
      </section>

      <section className="content px-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3">
            <div className="row align-items-center">
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <div className="input-group-prepend"><span className="input-group-text bg-light border-right-0"><i className="fas fa-search text-muted"></i></span></div>
                  <input type="text" className="form-control border-left-0 bg-light" placeholder="Search by name or brand..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 text-sm">
                <thead className="bg-light">
                  <tr>
                    <th className="text-center" width="60">Sr.</th>
                    <th>Product Name</th>
                    <th>Grade</th>
                    <th>Brand</th>
                    <th className="text-right">Price (₹)</th>
                    <th className="text-center">Stock</th>
                    <th className="text-center">Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={item.id}>
                      <td className="text-center text-muted">{indexOfFirstItem + index + 1}</td>
                      <td className="font-weight-bold">{item.name}</td>
                      <td><span className="badge badge-light border">{item.grade}</span></td>
                      <td>{item.company}</td>
                      <td className="text-right font-weight-bold text-primary">₹{item.price}</td>
                      <td className="text-center font-weight-bold">{item.stock} Units</td>
                      <td className="text-center">
                        {item.stock <= 5 ? 
                          <span className="badge badge-danger">Low Stock</span> : 
                          <span className="badge badge-success">In Stock</span>
                        }
                      </td>
                      <td className="text-right">
                        <button className="btn btn-xs btn-info mr-1" onClick={() => { setEditId(item.id); setFormData(item); }} data-toggle="modal" data-target="#itemModal"><i className="fas fa-edit"></i></button>
                        <button className="btn btn-xs btn-danger" onClick={() => handleDelete(item.id)}><i className="fas fa-trash"></i></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination Footer */}
          <div className="card-footer bg-white d-flex justify-content-between">
            <small className="text-muted">Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredItems.length)} of {filteredItems.length} entries</small>
            <nav>
              <ul className="pagination pagination-sm m-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>

      {/* Modal for Add/Edit */}
      <div className="modal fade" id="itemModal" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow">
            <div className="modal-header bg-navy text-white">
              <h5 className="modal-title">{editId ? 'Update Product' : 'Add New Lubricant'}</h5>
              <button type="button" className="close text-white" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <label className="small font-weight-bold">Product Name</label>
                  <input type="text" className="form-control" placeholder="e.g. Engine Oil 4T" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group mb-3">
                      <label className="small font-weight-bold">Grade</label>
                      <input type="text" className="form-control" placeholder="e.g. 20W40" value={formData.grade} onChange={e => setFormData({...formData, grade: e.target.value})}  />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-3">
                      <label className="small font-weight-bold">Brand/Company</label>
                      <input type="text" className="form-control" placeholder="Castrol, HP, etc." value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})}  />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group mb-3">
                      <label className="small font-weight-bold">Selling Price (₹)</label>
                      <input type="number" className="form-control" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})}  />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-3">
                      <label className="small font-weight-bold">Opening Stock</label>
                      <input type="number" className="form-control" value={formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})}  />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary btn-sm" data-dismiss="modal">Close</button>
                <button type="submit" disabled={loading} className="btn btn-primary btn-sm px-4">
                  {loading ? 'Saving...' : 'Save Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bg-navy { background-color: #001f3f; }
        .text-navy { color: #001f3f; }
        .btn-navy { background-color: #001f3f; color: #fff; }
        .table td { vertical-align: middle; }
      `}} />
    </div>
  );
}

export default Lubricantsinventory;